import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import React, { useState } from "react";
import TerminalLog from "../components/TerminalLog";
import { IOrchestrationInstanceSummary } from "../declarations/IOrchestrationInstance";
import { ISubStep } from "../declarations/IFunctionsResponse";
import { usePurgeOrchestration } from "../hooks/Starters/usePurgeOrchestration";
import { useTerminateOrchestration } from "../hooks/Starters/useTerminateOrchestration";
import {
    getDisplayStatus,
    getFailureMessage,
    hasFailed,
} from "./orchestrationStatus";

interface IChildOrchestrationMap {
    [phaseName: string]: ISubStep[];
}

interface IProcessDetailViewProps {
    childOrchestrations?: IChildOrchestrationMap;
    detail: IOrchestrationInstanceSummary;
    isLoading: boolean;
}

type StatusColor = "primary" | "success" | "error" | "warning" | "default";

function getStatusColor(status: string): StatusColor {
    switch (status) {
        case "Running":
            return "primary";
        case "Completed":
            return "success";
        case "Failed":
            return "error";
        case "Terminated":
            return "warning";
        default:
            return "default";
    }
}

function getHumanReadableName(name: string): string {
    return name.replace(/([A-Z])/g, " $1").trim();
}

export default function ProcessDetailView({
    childOrchestrations,
    detail,
    isLoading,
}: IProcessDetailViewProps) {
    const terminateOrchestration = useTerminateOrchestration();
    const purgeOrchestration = usePurgeOrchestration();
    const [copied, setCopied] = useState(false);

    if (isLoading) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>Loading orchestration details...</Typography>
                <LinearProgress sx={{ mt: 2 }} />
            </Box>
        );
    }

    if (!detail) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography sx={{ color: "text.secondary" }}>
                    Orchestration not found.
                </Typography>
            </Box>
        );
    }

    const isTerminal =
        detail.runtimeStatus === "Completed" ||
        detail.runtimeStatus === "Failed" ||
        detail.runtimeStatus === "Terminated";

    const isRunning = detail.runtimeStatus === "Running";
    // Not detail.runtimeStatus - a caught failure reports "Completed". See orchestrationStatus.
    const isFailed = hasFailed(detail);
    const displayStatus = getDisplayStatus(detail);
    const failureMessage = getFailureMessage(detail);

    const phase = detail.customStatus?.currentPhase ?? "Starting...";
    const percent = detail.customStatus?.percentComplete ?? 0;

    const handleCopyInstanceId = () => {
        navigator.clipboard.writeText(detail.instanceId);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleTerminate = () => {
        terminateOrchestration.mutate({
            instanceId: detail.instanceId,
            reason: "Terminated by user from Process Monitor",
        });
    };

    const handlePurge = () => {
        purgeOrchestration.mutate(detail.instanceId);
    };

    // Parse serialized output for display
    let outputData: any = null;
    if (detail.serializedOutput) {
        try {
            outputData = JSON.parse(detail.serializedOutput);
        } catch {
            outputData = detail.serializedOutput;
        }
    }

    return (
        <Box sx={{ p: 3 }}>
            <Stack spacing={3}>
                {/* Header */}
                <Box>
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            alignItems: "center",
                            mb: 1,
                        }}
                    >
                        <Typography variant="h6">
                            {getHumanReadableName(detail.name)}
                        </Typography>
                        <Chip
                            color={getStatusColor(displayStatus)}
                            label={displayStatus}
                            size="small"
                        />
                    </Stack>

                    <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{
                            alignItems: "center",
                            mb: 1,
                        }}
                    >
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            Instance ID: {detail.instanceId}
                        </Typography>
                        <Tooltip title={copied ? "Copied!" : "Copy Instance ID"}>
                            <IconButton onClick={handleCopyInstanceId} size="small">
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>

                    {detail.customStatus?.startedByUser && (
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            Started by: {detail.customStatus.startedByUser}
                        </Typography>
                    )}

                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Created: {new Date(detail.createdAt).toLocaleString()}
                    </Typography>

                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Last Updated:{" "}
                        {new Date(detail.lastUpdatedAt).toLocaleString()}
                    </Typography>
                </Box>

                {/* Progress */}
                <Box>
                    <Typography gutterBottom variant="body1">
                        {phase}
                    </Typography>
                    <LinearProgress
                        sx={{ height: 10, borderRadius: 1 }}
                        value={percent}
                        variant="determinate"
                    />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        {percent}%
                    </Typography>
                </Box>

                {/* Terminal Log */}
                <TerminalLog
                    childOrchestrations={childOrchestrations}
                    currentPhase={detail.customStatus?.currentPhase}
                    customCompletionMessage={
                        displayStatus === "Completed"
                            ? "Workflow completed successfully"
                            : displayStatus === "Failed"
                              ? "Workflow failed"
                              : displayStatus === "Terminated"
                                ? "Process terminated"
                                : undefined
                    }
                    isCompleted={isTerminal}
                />

                {/* Error Display */}
                {isFailed && failureMessage && (
                    <Box
                        sx={{
                            backgroundColor: "rgba(211, 47, 47, 0.08)",
                            border: "1px solid",
                            borderColor: "error.main",
                            borderRadius: 1,
                            padding: 2,
                        }}
                    >
                        <Typography
                            color="error"
                            gutterBottom
                            variant="subtitle2"
                        >
                            Error Details
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily:
                                    "Consolas, Monaco, 'Courier New', monospace",
                                fontSize: "13px",
                                whiteSpace: "pre-wrap",
                            }}
                            color="error"
                            variant="body2"
                        >
                            {failureMessage}
                        </Typography>
                    </Box>
                )}

                {/* Output Viewer */}
                {outputData && (
                    <Box>
                        <Typography gutterBottom variant="h6">
                            Output:
                        </Typography>
                        <Box
                            sx={{
                                backgroundColor: "#1e1e1e",
                                border: "1px solid #333",
                                borderRadius: 1,
                                color: isFailed ? "#f48771" : "#d4d4d4",
                                fontFamily:
                                    "Consolas, Monaco, 'Courier New', monospace",
                                fontSize: "13px",
                                maxHeight: "300px",
                                overflowY: "auto",
                                padding: 2,
                                whiteSpace: "pre-wrap",
                            }}
                        >
                            <Typography
                                sx={{
                                    color: "inherit",
                                    fontFamily: "inherit",
                                    fontSize: "inherit",
                                }}
                            >
                                {typeof outputData === "string"
                                    ? outputData
                                    : JSON.stringify(outputData, null, 2)}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* Actions */}
                <Stack direction="row" spacing={2}>
                    {isRunning && (
                        <Button
                            color="warning"
                            disabled={terminateOrchestration.isPending}
                            loading={terminateOrchestration.isPending}
                            onClick={handleTerminate}
                            startIcon={<StopCircleIcon />}
                            variant="contained"
                        >
                            Terminate
                        </Button>
                    )}
                    {isTerminal && (
                        <Button
                            color="error"
                            disabled={purgeOrchestration.isPending}
                            loading={purgeOrchestration.isPending}
                            onClick={handlePurge}
                            startIcon={<DeleteForeverIcon />}
                            variant="outlined"
                        >
                            Purge
                        </Button>
                    )}
                </Stack>
            </Stack>
        </Box>
    );
}
